import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interSemiBold, interMedium, interRegular } from "../../lib/fonts";
import { generateDigestSlug, getShortId, createSlug } from '../../lib/paperUrl';
import { PaperData } from '../papers/types';
import PaperCard from '../papers/PaperCard';
import { Trans } from '@lingui/react/macro';

interface DigestPaper {
    rank_position: number;
    project_paper_id: string;
    project_paper: {
        id: string;
        short_id: string | null;
        slug: string | null;
        paper_title?: string | null;
        summary: string | null;
        relevance_score: number | null;
        relevance_class: number | null;
        matching_details: any;
        personalized_note: string | null;
        paper: PaperData;
    };
}

interface DigestPaperListProps {
    papers: DigestPaper[];
    issueId: string;
    projectName: string;
}

/**
 * DigestPaperList displays a ranked list of papers in a digest issue
 * Each paper shows rank, title, authors, and summary preview
 */
export default function DigestPaperList({
    papers,
    issueId,
    projectName,
}: DigestPaperListProps) {
    const { fontsLoaded } = useFontLoading();
    const digestSlug = generateDigestSlug(projectName);
    // Ensure issueId is in short_id format for canonical URLs
    const digestShortId = issueId.includes('-') ? getShortId(issueId) : issueId;

    const renderPaper = ({ item }: { item: DigestPaper }) => {
        const paper = item.project_paper.paper;
        const summary = item.project_paper.summary;
        const relevanceScore = item.project_paper.relevance_score;
        const relevanceClass = item.project_paper.relevance_class;

        // Build canonical paper URL with paper slug
        const paperShortId = item.project_paper.short_id || getShortId(item.project_paper.id);
        const paperSlug = item.project_paper.slug
            || (item.project_paper.paper_title ? createSlug(item.project_paper.paper_title) : '')
            || (paper.title ? createSlug(paper.title) : 'paper');
        const paperPath = `/digest/${digestShortId}/${digestSlug}/p/${paperShortId}/${paperSlug}`;

        return (
            <PaperCard
                paper={paper}
                linkHref={paperPath}
                showRank={true}
                rankPosition={item.rank_position}
                showScores={true}
                relevanceScore={relevanceScore}
                relevanceClass={relevanceClass}
                showSummary={true}
                summaryContent={summary}
            />
        );
    };

    if (!papers || papers.length === 0) {
        return (
            <View style={styles.emptyState}>
                <MaterialIcons name="inbox" size={48} color={colors.text500} />
                <Text
                    style={[
                        styles.emptyText,
                        fontsLoaded && interRegular
                    ]}
                ><Trans id="digest.digestpaperlist.noPapersInThisDigestIssue" comment="UI copy in components/digest/DigestPaperList.tsx">
                    No papers in this digest issue
                </Trans></Text>
            </View>
        );
    }

    return (
        <View style={styles.listContainer}>
            {papers.map((item) => (
                <View key={item.project_paper_id}>
                    {renderPaper({ item })}
                </View>
            ))}
        </View>
    );
}

const styles = StyleSheet.create({
    listContainer: {
        gap: 16,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
        gap: 16,
    },
    emptyText: {
        fontSize: 16,
        color: colors.text500,
    },
});
